// @givmo/app-charity — the decoders for the caller's memberships read (CPA-WEB2; the third answer
// CPA-WEB48).
//
// `GET /charity-admin/memberships` returns a NAMED wrapper (`CharityMembershipListOut`,
// `{ memberships:[…] }`) that the api-client envelope middleware does NOT unwrap (it is shape-gated on
// the paginated `{data} + meta|links` envelope). The read is decoded here, accepting the same three
// shapes the shared `extractList` (`@givmo/api-client/list`) accepts — the named wrapper, a bare
// array, or the un-unwrapped `{data}` envelope — and never throwing on a surprising body.
//
// ⚠ A 200 WE CANNOT PARSE IS AN ERROR, NOT AN ABSENCE (CPA-WEB48). The tolerant `extractMemberships`
// answers `[]` for ANY body it does not recognise, and `[]` is what "this person administers
// nothing" looks like — the state that sends a caller to the claim door. So the memberships read
// itself (`org/active-org-context.tsx`, the one reader) decodes through `decodeMemberships`, which
// keeps the three answers apart: recognised-and-empty, recognised-and-populated, UNRECOGNISED. Only
// the first two may ever become `loaded-empty`/`ready`; the third fails closed to the retryable
// error state (`OffContractMembershipsError`, thrown by the read's `queryFn`). `extractMemberships`
// keeps its tolerant answer for every other caller and stays byte-for-byte `extractList`'s.
//
// Nothing else is decoded here: admin management is the Admin Console's Team access page, on the
// org-console package's own `/org/{ref}/members` wire (the legacy `/admins` page and its
// `/charity-admin/…/admins` + `…/invites` decoders retired with CPA-WEB41).

import type { CharityMembershipOut } from '@givmo/api-client';

/** The memberships read's three answers (CPA-WEB48). `unrecognised` must never read as empty. */
export type MembershipsDecode =
  | { kind: 'recognised'; memberships: CharityMembershipOut[] }
  | { kind: 'unrecognised' };

/** Narrow an unknown value to a plain object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function recognised(memberships: unknown[]): MembershipsDecode {
  return { kind: 'recognised', memberships: memberships as CharityMembershipOut[] };
}

/**
 * The STRICT decode of `GET /charity-admin/memberships` (`CharityMembershipListOut`): the same three
 * shapes `extractList(body, 'memberships')` reads, in the same order — a bare array, the named
 * `{ memberships:[…] }` wrapper, the un-unwrapped `{ data:[…] }` envelope — each answering
 * `recognised` (empty or not); anything else — `null`, `undefined`, a string, a number, an object
 * carrying none of those arrays — answers `unrecognised`. Never throws.
 */
export function decodeMemberships(body: unknown): MembershipsDecode {
  if (Array.isArray(body)) return recognised(body);
  if (isRecord(body)) {
    const named = body.memberships;
    if (Array.isArray(named)) return recognised(named);
    const data = body.data;
    if (Array.isArray(data)) return recognised(data);
  }
  return { kind: 'unrecognised' };
}

/**
 * The caller's memberships from `GET /charity-admin/memberships` — the TOLERANT reader (`[]` for a
 * body it does not recognise), for consumers that hold a body the read has already vetted. The
 * read itself must not derive an access state from this: see {@link decodeMemberships}.
 */
export function extractMemberships(body: unknown): CharityMembershipOut[] {
  const decoded = decodeMemberships(body);
  return decoded.kind === 'recognised' ? decoded.memberships : [];
}

/**
 * The memberships read answered 200 with a body {@link decodeMemberships} does not recognise. Thrown
 * by the read's `queryFn` so React Query treats the answer exactly as a failed request — retried by
 * the app's ordinary policy, surfaced as `isError`, the last good list (if any) kept in hand, and
 * "Try again" the way back — and never as an empty list.
 */
export class OffContractMembershipsError extends Error {
  constructor() {
    super('The memberships read answered with a body this app does not recognise.');
    this.name = 'OffContractMembershipsError';
  }
}
